import type { IncomingHttpHeaders } from 'http';
import type { AxiosResponse } from 'axios';
import getConfig from 'next/config';

import { donedealServicesGateway } from '../api/api-utils/commonApiUtils';
import type {
  IGetAdsRequest,
  IGetAdsResponseV2,
  IGetFiltersResponse,
  GetFiltersWithCountRequest,
  GetModelsWithCountsRequest,
  GetTrimsWithCountsRequest,
} from '../api/types/searchPageApiTypes';
import { IGetAdsResponse, IGetSection } from '../api/types/searchPageApiTypes';
import { IGetSearchFilterMakeModel } from '../types';

async function getAds(
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetAdsResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetAdsResponse> =
    await donedealServicesGateway.post(`/ddapi/v1/search`, payload, {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
    });

  return response;
}

async function getAdsV2(
  payload: IGetAdsRequest,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetAdsResponseV2>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetAdsResponseV2> =
    await donedealServicesGateway.post(`/ddapi/v2/search`, payload, {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
    });

  return response;
}

async function getFiltersWithCount({
  section,
  headers = { accept: 'application/json' },
  payload,
  timeout,
}: GetFiltersWithCountRequest): Promise<AxiosResponse<IGetFiltersResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const resolvedTimeout = timeout ?? parseInt(API_LISTINGS_TIMEOUT);
  return await donedealServicesGateway.post(
    `/ddapi/v4/search/filters/${section}`,
    {
      ...payload,
      sections: [section],
    },
    {
      headers: {
        ...headers,
      },
      timeout: resolvedTimeout,
    },
  );
}

async function getCarsFiltersWithCount(
  timeout?: number,
  body: {
    ranges?: {
      from: string | undefined;
      to: string | undefined;
      name: string;
    }[];
  } = {},
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const resolvedTimeout = timeout ?? parseInt(API_LISTINGS_TIMEOUT);
  return await donedealServicesGateway.post(
    `/ddapi/v4/search/filters/cars`,
    {
      sections: ['cars'],
      ...body,
    },
    {
      headers: { accept: 'application/json' },
      timeout: resolvedTimeout,
    },
  );
}

async function getFilters(
  section: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: number,
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const resolvedTimeout = timeout ?? parseInt(API_LISTINGS_TIMEOUT);
  return await donedealServicesGateway.get(
    `/ddapi/v4/search/filters/${section}`,
    {
      headers: {
        ...headers,
      },
      timeout: resolvedTimeout,
    },
  );
}

async function getFiltersC2B(
  headers: IncomingHttpHeaders = { accept: 'application/json' },
): Promise<AxiosResponse<IGetFiltersResponse>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetFiltersResponse> =
    await donedealServicesGateway.get('ddapi/v1/search/filters/cars-c2b', {
      headers: {
        ...headers,
        accept: 'application/json',
      },
      timeout: parseInt(API_LISTINGS_TIMEOUT),
    });
  return response;
}

async function getModelByMakeWithCounts({
  section,
  make,
  payload,
  headers = { accept: 'application/json' },
  timeout,
}: GetModelsWithCountsRequest): Promise<
  AxiosResponse<IGetSearchFilterMakeModel>
> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.post(
      `/ddapi/v4/search/filters/${section}/${make}`,
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getModelByMake(
  section: string,
  make: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  dealerId: string | null = null,
  timeout?: string,
): Promise<AxiosResponse<IGetSearchFilterMakeModel>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.get(
      dealerId
        ? `/ddapi/v1/search/showroom/filters/${section}/${make}?dealerId=${dealerId}`
        : `/ddapi/v1/search/filters/${section}/${make}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );

  return response;
}

async function getTrimByMakeModel(
  section: string,
  make: string,
  model: string,
  headers: IncomingHttpHeaders = { accept: 'application/json' },
  timeout?: string,
): Promise<AxiosResponse<IGetSearchFilterMakeModel>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.get(
      `/ddapi/v4/search/filters/${section}/${make}/${model}`,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getTrimByMakeModelWithCount({
  section,
  make,
  model,
  payload,
  headers = { accept: 'application/json' },
  timeout,
}: GetTrimsWithCountsRequest): Promise<
  AxiosResponse<IGetSearchFilterMakeModel>
> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetSearchFilterMakeModel> =
    await donedealServicesGateway.post(
      `/ddapi/v4/search/filters/${section}/${make}/${model}`,
      payload,
      {
        headers: {
          ...headers,
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );
  return response;
}

async function getSections(
  section = 'all',
  payload: IGetAdsRequest,
  timeout?: string,
): Promise<AxiosResponse<IGetSection>> {
  const {
    publicRuntimeConfig: { API_LISTINGS_TIMEOUT },
  } = getConfig();
  const response: AxiosResponse<IGetSection> =
    await donedealServicesGateway.post(
      `/ddapi/v1/search/sections/${section}`,
      payload,
      {
        headers: {
          accept: 'application/json',
        },
        timeout: parseInt(timeout || API_LISTINGS_TIMEOUT),
      },
    );

  return response;
}

export const searchPageApi = {
  getAds,
  getAdsV2,
  getFilters,
  getFiltersWithCount,
  getFiltersC2B,
  getModelByMake,
  getModelByMakeWithCounts,
  getTrimByMakeModel,
  getTrimByMakeModelWithCount,
  getSections,
  getCarsFiltersWithCount,
};
